import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DeleteIcon } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'
import { differenceInCalendarDays, isAfter } from 'date-fns'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getPeriodsByAvailability } from '../../../api/periods/get-period-by-availability'
import { getPeriods } from '../../../api/periods/get-periods'
import useTranslate from '../../../lib/hooks/use-translate'
import { StaffPositionEnum } from '../../../lib/types/enums/staff/staff-position.enum'
import { AvailabilityInput } from '../../../lib/types/models/availability/availability-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { getPeriodsDates } from '../../../views/staff-personal-space/utils'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import PeriodBadge from '../../elements/period-badge'
import ValidationButtons from '../../elements/validation-buttons'

type Props = {
  className?: string
  defaultValues?: Partial<AvailabilityInput>
  isLoading?: boolean
  submitText?: string
  cancelText?: string
  handleSubmit: (values: AvailabilityInput) => void
  onDelete?: () => void
  onCancel: () => void
  position?: StaffPositionEnum
}

/**
 * Minimum number of days between the start and the end of an availability.
 * Mirrors the backend rule: AAT staff only accompany the travel, so they may
 * register an availability of a single day, every other position keeps 6 days.
 */
export const getMinimumAvailabilityDaysDifference = (position?: StaffPositionEnum): number =>
  position === StaffPositionEnum.AAT ? 0 : 6

const createFormSchema = (minimumDaysDifference: number): ZodType<AvailabilityInput> =>
  z
    .object({
      startDate: createDateStringSchema({ inputName: 'date de début' }),
      endDate: createDateStringSchema({ inputName: 'date de fin' })
    })
    .superRefine((data, ctx) => {
      if (data.startDate && data.endDate && isAfter(data.startDate, data.endDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La date de début doit être avant la date de fin',
          path: ['startDate']
        })
        return
      }

      // The period should last at least the minimum allowed for the staff position
      if (
        differenceInCalendarDays(
          DateFormatter.toDate(data.endDate),
          DateFormatter.toDate(data.startDate)
        ) < minimumDaysDifference
      )
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `La période doit être d'au moins ${minimumDaysDifference} jours`,
          path: ['endDate']
        })
    })

export function StaffAvailabilityForm({
  className,
  defaultValues,
  isLoading,
  submitText,
  cancelText,
  handleSubmit,
  onDelete,
  onCancel,
  position
}: Props) {
  const formSchema = useMemo(
    () => createFormSchema(getMinimumAvailabilityDaysDifference(position)),
    [position]
  )

  const form = useForm<AvailabilityInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })
  const { t } = useTranslate()

  const { watch } = form
  const { startDate, endDate } = watch()

  const { data: periods } = useQuery({
    queryKey: ['periods', 'LIGHT'],
    queryFn: () => getPeriods({ version: 'LIGHT' })
  })

  const { data, isFetching } = useQuery({
    queryKey: ['staff-availabilities', startDate, endDate],
    queryFn: () => {
      const formattedStartDate = DateFormatter.toDateFormatted(DateFormatter.toDate(startDate))
      const formattedEndDate = DateFormatter.toDateFormatted(DateFormatter.toDate(endDate))

      return getPeriodsByAvailability({ startDate: formattedStartDate, endDate: formattedEndDate })
    },
    enabled: !!startDate && !!endDate
  })

  const allowedDates = getPeriodsDates(periods?.data || [])

  const onSubmit = (values: AvailabilityInput) => {
    handleSubmit({ ...values, periods: data })
  }

  return (
    <Form {...form}>
      <form id="myForm" onSubmit={form.handleSubmit(onSubmit)}>
        <div className={cn('flex flex-col gap-2', className)}>
          {isFetching ? (
            <Badge loading={true} className="mt-2" />
          ) : data?.length ? (
            <div className="flex flex-wrap gap-2 mt-2">
              {data.map((period) => (
                <PeriodBadge key={period.id} period={period} />
              ))}
            </div>
          ) : null}
          <div className="h-2" />
          <div className={'flex flex-row gap-2'}>
            <FormField
              control={form.control}
              name={'startDate'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      allowedDates={allowedDates}
                      mode="over"
                      label={t('staff.personal.space.availability.form.date.from')}
                      required
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'endDate'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      allowedDates={allowedDates}
                      mode="over"
                      label={t('staff.personal.space.availability.form.date.to')}
                      startDate={new Date(startDate)}
                      required
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <ValidationButtons
          hasBorderTop
          buttonsConfig={modalButtonConfig({
            onCancel,
            onConfirm: onSubmit,
            isLoading,
            confirmText: submitText,
            cancelText
          })}
          preComponent={
            onDelete && (
              <Button
                disabled={isLoading}
                variant="destructive"
                type="button"
                label={t('button.delete')}
                icon={DeleteIcon}
                aria-label={t('button.delete')}
                onClick={onDelete}
              />
            )
          }
        />
      </form>
    </Form>
  )
}
